import { PremadeGameSettings } from '../../../../shared/PremadeGameSettings.ts';
import { CA } from '../analytics/Analytics.ts';
import { UIGameSpriteHandler } from '../UIGameSpriteHandler.ts';
import { AnalyticEventCategory, AnalyticEventType, JoinRoomMethod } from '../../../../shared/entity_datas/EntityDatas.ts';
import { socket } from '../EntryShared.ts';
import { ChannelAction, LobbyAction, PRIMARY_CHANNEL_ACTIONS, PRIMARY_LOBBY_ACTIONS } from '@colonist/lobby-types';
import { BotDifficulty } from '@colonist/game-base-types';
import { AbortError, ClientMessageSubType, COLONIST_VERSION } from '@colonist/types';
import { BeginnerModeLevel, getBotsForBeginnerModeLevel, getMapForBeginnerModeLevel } from '@colonist/game-beginner-mode';
import { StorageWrapper } from '../StorageWrapper.ts';
import { GeneralAction } from '../../../../shared/friends/FriendTypes.ts';
import { UIUtilsFriends } from '../ui_friends/UIUtilsFriends.ts';
import { UIUtilsNotifications } from '../notifications/UIUtilsNotifications.ts';
import { ClientUserState } from '../ClientUserState.ts';
export class SocketLobbySend {
    static interactedWithSite() {
        this.sendLobby(LobbyAction.InteractedWithSite, true);
    }
    static setAdBlockStatusToUserSession(adsBlocked) {
        this.sendLobby(LobbyAction.SetAdBlockStatus, adsBlocked);
    }
    static async continueCampaignMode() {
        await this.waitUntilGameCanBeStarted();
        const level = StorageWrapper.getBeginnerModeLevel();
        if (level == BeginnerModeLevel.End) return this.start1v1BeginnerBotGame();
        this.startBeginnerMode(level);
    }
    static async clickedStartBotGame(botDifficulty, action, mapSetting) {
        await this.waitUntilGameCanBeStarted();
        const data = this.createStartBotGameData(botDifficulty, mapSetting);
        this.sendLobby(action, data);
    }
    static createStartBotGameData(botDifficulty, mapSetting) {
        const data = {
            clientVersion: COLONIST_VERSION,
            bots: PremadeGameSettings.getBotGameBots(botDifficulty),
            difficulty: botDifficulty
        };
        if (mapSetting != undefined) data.map = mapSetting;
        return data;
    }
    static registerToFriendService() {
        if (!UIUtilsFriends.canUseOnlineFriendService()) return;
        this.sendGeneral(GeneralAction.RegisterToFriendService, {});
    }
    static changedOnlineStatus(newOnlineStatus) {
        if (!UIUtilsFriends.canUseOnlineFriendService()) return;
        SocketLobbySend.sendGeneral(GeneralAction.ChangeOnlineStatus, newOnlineStatus);
    }
    static getAllFriendsOnlineStatus() {
        if (!UIUtilsFriends.canUseOnlineFriendService()) return;
        SocketLobbySend.sendGeneral(GeneralAction.GetAllFriendsOnlineStatus, {});
    }
    static inviteFriendToRoom(receiverUserId, roomId) {
        if (!UIUtilsFriends.canUseOnlineFriendService()) return;
        const data = {
            receiverUserId: receiverUserId,
            roomId: roomId
        };
        this.sendGeneral(GeneralAction.InviteFriendToRoom, data);
    }
    static respondToFriendRoomInvite(roomId, accept, senderUserId) {
        if (!UIUtilsFriends.canUseOnlineFriendService()) return;
        const data = {
            roomId: roomId,
            accept: accept,
            senderUserId: senderUserId
        };
        this.sendGeneral(GeneralAction.RespondToFriendRoomInvite, data);
    }
    static getAllFriendRoomInvitesReceived() {
        if (!UIUtilsFriends.canUseOnlineFriendService()) return;
        this.sendGeneral(GeneralAction.GetAllRoomInvitesReceived, {});
    }
    static registerToNotificationService() {
        if (!UIUtilsNotifications.canUseLiveNotificationService()) return;
        this.sendGeneral(GeneralAction.RegisterToNotificationService, {});
    }
    static markAllNotificationsAsRead() {
        if (!UIUtilsNotifications.canUseLiveNotificationService()) return;
        this.sendGeneral(GeneralAction.MarkAllNotificationsAsRead, {});
    }
    static markNotificationAsRead(notificationId) {
        if (!UIUtilsNotifications.canUseLiveNotificationService()) return;
        this.sendGeneral(GeneralAction.MarkNotificationAsRead, notificationId);
    }
    static getNotifications(getNotificationData) {
        if (!UIUtilsNotifications.canUseLiveNotificationService()) return;
        this.sendGeneral(GeneralAction.GetNotifications, getNotificationData);
    }
    static watchRoomList() {
        this.sendLobby(LobbyAction.WatchRoomList, {});
    }
    static stopWatchingRoomList() {
        this.sendLobby(LobbyAction.StopWatchingRoomList, {});
    }
    static clickedCreateRoom(roomType, isPrivate) {
        this.sendAnalyticEvent(AnalyticEventType.JoinedRoom, AnalyticEventCategory.Room, undefined, JoinRoomMethod.ClickedCreateRoom);
        const data = {
            roomType: roomType,
            clientVersion: COLONIST_VERSION,
            isPrivate: isPrivate
        };
        this.sendLobby(LobbyAction.ClickedCreateRoom, data);
    }
    static async clickedJoinRoom(roomId) {
        await this.waitUntilGameCanBeStarted();
        this.sendAnalyticEvent(AnalyticEventType.JoinedRoom, AnalyticEventCategory.Room, roomId, JoinRoomMethod.ClickedLobbyRoom);
        const data = {
            gameId: roomId,
            clientVersion: COLONIST_VERSION
        };
        this.sendLobby(LobbyAction.ClickedJoinRoom, data);
    }
    static async clickedSpectateGame(gameId) {
        await this.waitUntilGameCanBeStarted();
        const data = {
            gameId: gameId,
            clientVersion: COLONIST_VERSION
        };
        this.sendLobby(LobbyAction.ClickedSpectateGame, data);
    }
    static reconnectGame(gameId) {
        void UIGameSpriteHandler.continueAfterAssetsLoad(()=>{
            const data = {
                gameId: gameId,
                clientVersion: COLONIST_VERSION
            };
            this.sendLobby(LobbyAction.ReconnectGame, data);
        });
    }
    static autoRejoinGame() {
        this.sendLobby(LobbyAction.AutoRejoinGame, {});
    }
    static async accessGameFromLink(roomId) {
        this.sendAnalyticEvent(AnalyticEventType.JoinedRoom, AnalyticEventCategory.Room, roomId, JoinRoomMethod.EnteredRoomLink);
        try {
            await this.waitUntilGameCanBeStarted();
        } catch (error) {
            if (error instanceof AbortError) {
                this.updateURLToRoom('');
                return;
            }
            throw error;
        }
        const data = {
            gameId: roomId,
            clientVersion: COLONIST_VERSION
        };
        this.sendLobby(LobbyAction.AccessGameLink, data);
    }
    static startBeginnerMode(level) {
        const data = {
            clientVersion: COLONIST_VERSION,
            level: level,
            map: getMapForBeginnerModeLevel(level),
            bots: getBotsForBeginnerModeLevel(level)
        };
        this.sendLobby(LobbyAction.ClickedStartBeginnerMode, data);
    }
    static start1v1BeginnerBotGame() {
        const data = {
            clientVersion: COLONIST_VERSION,
            bots: [
                BotDifficulty.BotEasy
            ]
        };
        this.sendLobby(LobbyAction.Clicked1v1BeginnerBotGame, data);
    }
    static updateURLToRoom(gameId) {
        if (window.location.hash == '#' + gameId) return;
        window.location.assign('#' + gameId);
    }
    // ====================================================================================================================================
    // Channel
    // ====================================================================================================================================
    static joinChannel(channelId, serverProfile, isInitialLoad, roomOrGameId) {
        void UIGameSpriteHandler.continueAfterAssetsLoad(()=>{
            const data = {
                channelId: channelId,
                isInitialLoad: isInitialLoad,
                roomOrGameId: roomOrGameId,
                ...serverProfile
            };
            this.sendChannel(ChannelAction.ChannelJoin, data);
        }, ()=>{
            this.updateURLToRoom('');
        });
    }
    static clickedJoinChannelRoomOrGame(channelId, roomType) {
        // Maybe add analytics here?
        void UIGameSpriteHandler.continueAfterAssetsLoad(()=>{
            const data = {
                channelId: channelId,
                roomType: roomType
            };
            this.sendChannel(ChannelAction.ChannelJoinRoomOrGame, data);
        });
    }
    static sendLobby(action, payload) {
        const data = {
            action: action,
            payload: payload
        };
        socket.controller.sender.sendToServerType(ClientMessageSubType.LobbyAction, 'lobby', data, PRIMARY_LOBBY_ACTIONS.includes(action));
    }
    static sendChannel(action, payload) {
        const data = {
            action: action,
            payload: payload
        };
        socket.controller.sender.sendToServerType(ClientMessageSubType.ChannelAction, 'lobby', data, PRIMARY_CHANNEL_ACTIONS.includes(action));
    }
    static sendGeneral(action, payload) {
        const data = {
            action: action,
            payload: payload
        };
        socket.controller.sender.sendToServerType(ClientMessageSubType.GeneralAction, 'lobby', data, false);
    }
    static sendAnalyticEvent(type, category, stringValue, floatValue) {
        const event = {
            type: type,
            category: category,
            stringValue: stringValue,
            floatValue: floatValue
        };
        void CA.sendAnalyticEvent(event);
    }
    static async waitUntilGameCanBeStarted() {
        await ClientUserState.requireUser();
        await UIGameSpriteHandler.continueAfterAssetsLoad();
    }
}
